/**
 * Une campagne d'examen national : CEPE, BEPC ou baccalaureat.
 *
 * Les trois examens partageaient trois fichiers quasi identiques d'un millier
 * de lignes. Ce composant les remplace ; ce qui les distingue vraiment tient
 * dans `examensConfig.js`.
 */
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { 
  Search, 
  Users,
  Download, 
  Upload,
  FileText, 
  CheckCircle,
  XCircle,
  Loader2,
  Trash2,
  Eye,
  CreditCard,
  Calendar,
  User
} from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "./ui/dialog";

// Import du hook
import { useExamens } from '../hooks/useExamens';

import { useExamensContext } from '../contexts/ExamensContext';
import { api } from '../services/api';
import FilArianeExamen from './examens/FilArianeExamen';
import LoadingSpinner from './LoadingSpinner';
import EtatVide from './ui/EtatVide';
import Pagination from './ui/Pagination';
import ChampSelect from './ui/ChampSelect';
import PorteurNotification from './ui/PorteurNotification';
import ConfirmDialog from './ConfirmDialog';
import { useNotification } from '../hooks/useNotification';
import { formaterFCFA } from '../utils/montants';
import { telechargerPdf } from '../utils/telechargerPdf';
import { useResultatsExamen } from './examens/useResultatsExamen';
import ImportResultats from './examens/ImportResultats';
import {
  TableauResultats,
  SyntheseResultats,
  FILTRES_RESULTAT,
  resultatDe
} from './examens/ResultatsExamen';

/**
 * Puce de filtre par classe.
 *
 * La classe n'est plus une étape de navigation : elle réduit la liste sur
 * place, et le compteur dit tout de suite ce qu'on gagne à cliquer.
 */
const PuceClasse = ({ libelle, nombre, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${active
      ? 'border-[#246990] bg-[#246990] text-white'
      : 'border-gray-200 bg-white text-gray-600 hover:border-[#246990] hover:text-[#246990]'
      }`}
  >
    {libelle}
    <span className={active ? 'text-white/70' : 'text-gray-400'}>{nombre}</span>
  </button>
);

// Les deux boutons d'un pied de modal partagent taille ET largeur : un bouton
// d'annulation plus etroit suggere une hierarchie qui n'existe pas.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const STATUTS_DOSSIER = [
  { valeur: 'all', libelle: 'Tous les statuts' },
  { valeur: 'complete', libelle: 'Dossiers complets' },
  { valeur: 'pending', libelle: 'Dossiers en attente' },
  { valeur: 'incomplete', libelle: 'Dossiers incomplets' }
];

const MODES_PAIEMENT = [
  { valeur: 'especes', libelle: 'Espèces' },
  { valeur: 'cheque', libelle: 'Chèque' },
  { valeur: 'virement', libelle: 'Virement bancaire' },
  { valeur: 'mobile_money', libelle: 'Mobile Money' },
  { valeur: 'carte_bancaire', libelle: 'Carte bancaire' }
];

// ⚠️ LE CHEMIN D'UN DOCUMENT S'OUVRE TEL QUE LA BASE LE STOCKE, SANS PREFIXE.
//
// `chemin_fichier` vaut deja « /uploads/examens/<fichier> » : c'est
// `ExamenController.js` qui l'ecrit sous cette forme. Le prefixe pose ici,
// `process.env.REACT_APP_API_URL || 'http://localhost:5000'`, n'a JAMAIS
// designe quoi que ce soit de valable. La variable n'est posee nulle part —
// aucun `.env` dans le depot, et `docker-compose.yml` ne passe pas l'argument
// de compilation au service web — donc le repli s'appliquait TOUJOURS, en
// production comprise, ou il designe la machine du gestionnaire sur un port ou
// rien n'ecoute : le serveur est sur 3001, pas sur 5000. Le bouton « Voir »
// ouvrait donc un onglet mort, a tous les coups, pour toutes les ecoles, sans
// un mot dans l'application. Verifie le 04/10/2026 dans le paquet compile qui
// tourne en production : « localhost:5000 » y etait bien present.
//
// Le chemin relatif suit le domaine de la page — csap1 depuis csap1, csap2
// depuis csap2 — et nginx route /uploads vers Express sur les trois
// sous-domaines. C'est la seule forme correcte. Le meme correctif a ete
// applique a `CandidatsLibresSection.jsx`, qui porte la meme cellule copiee.
const SectionExamen = ({ examen, onRetourTableauBord, onChangerExamen }) => {
  const { code, libelleClasses, libelleLong } = examen;
  const { notification, notifier, masquer } = useNotification();

  // Les echecs de LECTURE remontent ici : sans cela une coupure reseau se
  // lisait « aucun eleve ».
  const { getElevesByExamen, getDetailsInscription, uploadDocument, chargements } =
    useExamens({ onErreur: (message) => notifier(message, 'error') });
  const { triggerRefresh } = useExamensContext();
  const [eleves, setEleves] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [page, setPage] = useState(1);
  const PAR_PAGE = 25;
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentFile, setShowStudentFile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Deux vues sur la même liste : le dossier d'inscription (pièces, frais) et,
  // une fois l'examen passé, le résultat. Classe et recherche valent pour les
  // deux ; chaque vue a son propre filtre d'état.
  const [vue, setVue] = useState('dossiers');
  const [filtreResultat, setFiltreResultat] = useState('all');
  // L'import de la liste officielle s'ouvre sur place, comme un dossier.
  const [importOuvert, setImportOuvert] = useState(false);
  const resultats = useResultatsExamen({
    code,
    actif: vue === 'resultats',
    onErreur: (message) => notifier(message, 'error')
  });
  const [detailsInscription, setDetailsInscription] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Deux actions distinctes sur une meme ligne : les confondre faisait
  // tourner le bouton de depot quand on cliquait seulement « Recue ».
  // { pieceId, recue } : sans le sens de l'action, le bouton « Recue »
  // s'animait aussi quand on cliquait « Non ».
  // Sans attente visible sur le bouton, l'utilisateur croit que rien ne se
  // passe et reclique : sur une suppression, cela double l'action.
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [ecritureEnCours, setEcritureEnCours] = useState(false);
  const [receptionEnCours, setReceptionEnCours] = useState(null);
  const [depotEnCours, setDepotEnCours] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  
  const [paiementForm, setPaiementForm] = useState({
    montant_paye: '',
    mode_paiement: 'especes',
    reference_paiement: '',
    notes: ''
  });


  const [exportEnCours, setExportEnCours] = useState(false);

  // Les filtres de l'ecran voyagent avec le PDF : une liste filtree qui ne le
  // dit pas se lit comme la liste complete.
  const handleExporter = async () => {
    setExportEnCours(true);
    try {
      const reponse = await api.examens.exporterListePDF({
        examenType: code,
        filtreStatut: statusFilter !== 'all'
          ? STATUTS_DOSSIER.find((s) => s.valeur === statusFilter)?.libelle
          : null,
        filtreClasse: selectedClass || null,
        filtreRecherche: searchTerm.trim() || null
      });
      const nom = await telechargerPdf(reponse, `Liste_${code}.pdf`);
      notifier(`Liste exportée : ${nom}`);
    } catch (erreur) {
      notifier(erreur.message || "L'export a échoué", 'error');
    } finally {
      setExportEnCours(false);
    }
  };

  // Conserve pour ne pas reecrire les 16 appels : le rendu, lui, passe
  // desormais par ToastPortal et bascule en LongToast quand le message est long.
  const showToast = (message, type = 'success') => notifier(message, type);


  // Calculer le statut d'un élève
  const calculateStatut = (student) => {
    const docsComplets = student.documents_fournis_count >= student.documents_requis_count;
    const paiementComplet = student.statut_paiement === 'paye';

    if (docsComplets && paiementComplet) return 'complete';
    if (docsComplets || paiementComplet) return 'pending';
    return 'incomplete';
  };


  
  // Charger les eleves du niveau concerne.
  //
  // `code` figure dans les dépendances : le composant sert désormais les trois
  // examens, et un tableau vide le ferait rester sur la première liste chargée
  // si l'examen changeait sans que le composant soit remonté.
  useEffect(() => {
    const fetchEleves = async () => {
      const data = await getElevesByExamen(code);
      setEleves(data);
    };
    fetchEleves();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grouper les élèves par classe.
  //
  // Les candidats viennent du serveur, qui seul décide qui se présente : les
  // classes cochées au paramétrage des examens, ou à défaut la règle du nom
  // (le BEPC y écarte les P3). Le navigateur écartait aussi les P3 de son
  // côté : une classe P3 cochée au paramétrage aurait disparu de l'écran.
  const classes = [...new Set(eleves.map(e => e.classe_nom))].sort();

  // Filtrer les élèves
  const filteredStudents = eleves.filter(student => {
    const matchesClass = selectedClass ? student.classe_nom === selectedClass : true;
    // Dans la vue Résultats, le matricule national se cherche aussi : c'est
    // lui qu'on lit sur la liste officielle. Même forme que le serveur le
    // garde, majuscules sans espace.
    const matriculeNational = vue === 'resultats'
      ? resultats.parEleve[student.id]?.matricule_national || ''
      : '';
    const rechercheIdentifiant = searchTerm.replace(/\s+/g, '').toUpperCase();
    const matchesSearch = student.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          student.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (rechercheIdentifiant !== '' && matriculeNational.includes(rechercheIdentifiant));

    let matchesStatus = true;
    if (vue === 'resultats') {
      if (filtreResultat !== 'all') {
        matchesStatus = resultatDe(resultats.parEleve, student.id) === filtreResultat;
      }
    } else if (statusFilter !== 'all') {
      const statut = calculateStatut(student);
      matchesStatus = statut === statusFilter;
    }
    
    return matchesClass && matchesSearch && matchesStatus;
  });


  const nombrePages = Math.max(1, Math.ceil(filteredStudents.length / PAR_PAGE));
  const elevesPage = filteredStudents.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);

  // Un filtre qui reduit la liste doit ramener a la premiere page : rester sur
  // la page 4 d'un resultat qui n'en compte plus qu'une affiche un vide.
  useEffect(() => {
    setPage(1);
  }, [selectedClass, statusFilter, searchTerm, vue, filtreResultat]);


 
  // Charger les détails d'une inscription
const handleStudentSelect = async (student) => {
  setSelectedStudent(student);
  setShowStudentFile(true);
  setLoadingDetails(true);
  
  try {
    // Essayer de charger les détails existants
    const details = await getDetailsInscription(student.id, code);
    
    if (details && details.inscription) {
      // ✅ L'inscription existe, on affiche les détails
      setDetailsInscription(details);
    } else {
      // ❌ L'inscription n'existe pas, on la crée
      // Le libellé disait « CEPE » dans les trois fichiers : BEPC et BAC en
      // étaient des copies, et personne n'avait repris ce journal.
      console.log(`Création de l'inscription ${code} pour`, student.nom, student.prenom);
      const inscriptionResult = await api.examens.inscrireEleve(student.id, code);
      
      if (!inscriptionResult.success) {
        showToast('Erreur lors de la création de l\'inscription: ' + inscriptionResult.error, 'error');
        setLoadingDetails(false);
        setShowStudentFile(false);
        return;
      }
      
      console.log('Inscription créée avec succès');
      triggerRefresh(); // Rafraîchir le dashboard
      // Recharger les détails après création
      const newDetails = await getDetailsInscription(student.id, code);
      if (newDetails) {
        setDetailsInscription(newDetails);
      } else {
        showToast('Impossible de charger les détails de l\'inscription', 'error');
        setShowStudentFile(false);
      }
    }
  } catch (error) {
    console.error('Erreur chargement détails:', error);
    
    // Si l'erreur est 404 (inscription non trouvée), on essaie de créer
    if (error.message.includes('404')) {
      try {
        console.log('Inscription non trouvée, création en cours...');
        const inscriptionResult = await api.examens.inscrireEleve(student.id, code);
        
        if (inscriptionResult.success) {
          const newDetails = await getDetailsInscription(student.id, code);
          if (newDetails) {
            setDetailsInscription(newDetails);
          }
        } else {
          showToast('Erreur: ' + inscriptionResult.error, 'error');
          setShowStudentFile(false);
        }
      } catch (createError) {
        showToast('Erreur lors de la création: ' + createError.message, 'error');
        setShowStudentFile(false);
      }
    } else {
      showToast('Erreur: ' + error.message, 'error');
      setShowStudentFile(false);
    }
  } finally {
    setLoadingDetails(false);
  }
};

  // Constater la reception d'une piece, sans fichier.
  //
  // C'est l'action principale du guichet : recevoir un acte de naissance ne
  // suppose pas de le scanner. Le fichier vient apres, s'il vient.
  const handleMarquerReception = async (documentRequisId, recue) => {
    setReceptionEnCours({ pieceId: documentRequisId, recue });
    try {
      const result = await api.examens.marquerReceptionPiece({
        eleve_id: selectedStudent.id,
        document_requis_id: documentRequisId,
        recue
      });

      if (result.success) {
        const details = await getDetailsInscription(selectedStudent.id, code);
        setDetailsInscription(details);

        // Le compteur de la ligne suit, sans recharger toute la liste.
        setEleves((prev) => prev.map((e) => e.id === selectedStudent.id
          ? {
            ...e,
            documents_fournis_count: Math.max(
              0, (e.documents_fournis_count || 0) + (recue ? 1 : -1)
            )
          }
          : e));

        triggerRefresh();
        notifier(result.message || (recue ? 'Piece marquee comme recue' : 'Piece retiree'));
      } else {
        notifier(result.message || result.error || 'Operation impossible', 'error');
      }
    } catch (erreur) {
      notifier(erreur.message || 'Operation impossible', 'error');
    } finally {
      setReceptionEnCours(null);
    }
  };

  // Upload de document
  const handleUploadDocument = async (documentRequisId, file) => {
    // La piece concernee, pas toutes : un drapeau unique faisait tourner les
    // boutons des lignes auxquelles on n'avait pas touche.
    setDepotEnCours(documentRequisId);
    try {
      const result = await uploadDocument(selectedStudent.id, documentRequisId, file);
      if (result.success) {
        // Recharger UNIQUEMENT les détails de l'étudiant
        const details = await getDetailsInscription(selectedStudent.id, code);
        setDetailsInscription(details);
        
        // Mettre à jour UNIQUEMENT cet élève dans la liste
        setEleves(prevEleves => 
          prevEleves.map(e => 
            e.id === selectedStudent.id 
              ? { ...e, documents_fournis_count: e.documents_fournis_count + 1 }
              : e
          )
        );
        
        showToast('Document uploadé avec succès');
        triggerRefresh();
      } else {
        showToast('Erreur lors de l\'upload: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      showToast('Erreur lors de l\'upload', 'error');
    } finally {
      setDepotEnCours(null);
    }
  };

  // Supprimer un document
  const handleDeleteDocumentClick = (doc) => {
    setDocumentToDelete(doc);
    setShowDeleteDocModal(true);
  };


  // Supprimer un document - VERSION OPTIMISÉE
    const handleConfirmDeleteDocument = async () => {
      if (documentToDelete) {
        setSuppressionEnCours(true);
        try {
          const result = await api.examens.deleteDocument(documentToDelete.id);
          if (result.success) {
            const details = await getDetailsInscription(selectedStudent.id, code);
            setDetailsInscription(details);
            
            setEleves(prevEleves => 
              prevEleves.map(e => 
                e.id === selectedStudent.id 
                  ? { ...e, documents_fournis_count: Math.max(0, e.documents_fournis_count - 1) }
                  : e
              )
            );
            
            showToast('Document supprimé avec succès');
            triggerRefresh();
          } else {
            showToast('Erreur: ' + result.error, 'error');
          }
        } catch (error) {
          showToast('Erreur: ' + error.message, 'error');
        }
      }
      setSuppressionEnCours(false);
      setShowDeleteDocModal(false);
      setDocumentToDelete(null);
    };


  // Ouvrir le modal de paiement
  const handleOpenPaymentModal = () => {
    if (detailsInscription?.paiement) {
      const reste = parseFloat(detailsInscription.paiement.montant_attendu) - 
                    parseFloat(detailsInscription.paiement.montant_paye || 0);
      setPaiementForm({
        montant_paye: reste > 0 ? reste : '',
        mode_paiement: 'especes',
        reference_paiement: '',
        notes: ''
      });
    }
    setShowPaymentModal(true);
  };

  // Enregistrer un paiement

  const handleSavePayment = async () => {
    if (!paiementForm.montant_paye || parseFloat(paiementForm.montant_paye) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setEcritureEnCours(true);
    try {
      const result = await api.examens.enregistrerPaiement({
        eleve_id: selectedStudent.id,
        examen_type: code,
        montant_paye: parseFloat(paiementForm.montant_paye),
        mode_paiement: paiementForm.mode_paiement,
        reference_paiement: paiementForm.reference_paiement,
        notes: paiementForm.notes
      });

      if (result.success) {
        // Le serveur renvoie le reste dû : l'annoncer évite d'avoir à rouvrir
        // le dossier pour savoir si le candidat est en règle.
        const reste = Number(result.data?.reste_du ?? 0);
        showToast(
          reste > 0
            ? `Paiement enregistré. Reste dû : ${formaterFCFA(reste)}`
            : 'Paiement enregistré. Frais soldés.'
        );
        setShowPaymentModal(false);
        
        const details = await getDetailsInscription(selectedStudent.id, code);
        setDetailsInscription(details);
        
        setEleves(prevEleves => 
          prevEleves.map(e => 
            e.id === selectedStudent.id 
              ? { 
                  ...e, 
                  statut_paiement: details.paiement?.statut || 'partiel',
                  montant_paye: details.paiement?.montant_paye || 0
                }
              : e
          )
        );
        
        triggerRefresh();
        
        setPaiementForm({
          montant_paye: '',
          mode_paiement: 'especes',
          reference_paiement: '',
          notes: ''
        });
      } else {
        // Le serveur refuse désormais un versement supérieur au reste dû, ou
        // sur des frais déjà soldés : son message est plus utile que le nôtre.
        showToast(result.message || result.error || 'Paiement refusé', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors du paiement', 'error');
    } finally {
      setEcritureEnCours(false);
    }
  };


  // Gestionnaires
  const handleBackToStudents = () => {
    setShowStudentFile(false);
    setDetailsInscription(null);
  };

  // Fonctions utilitaires

  const getStatusBadge = (status) => {
    const styles = {
      complete: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      incomplete: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      complete: 'Complet',
      pending: 'En attente',
      incomplete: 'Incomplet'
    };
    
    return (
      <span className={`inline-flex items-center justify-center w-[132px] h-6 rounded-full text-[10px] font-semibold whitespace-nowrap ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Compter les statuts par classe

  // On ne vide l'ecran QUE lorsqu'il n'y a encore rien a montrer. Au-dela, le
  // chargement ne vide plus rien : la liste reste lisible. C'est
  // ce qui evite que la page « s'actualise » a chaque depot de piece.
  const premierChargement = chargements.liste && eleves.length === 0;

  // Vue Résultats : spinner tant que la première lecture n'a pas répondu, et
  // plus de liste du tout quand la saisie n'est pas installée ou illisible.
  const vueResultats = vue === 'resultats';
  const resultatsEnAttente = vueResultats && resultats.disponible === null && !resultats.echec;
  const listeMasquee = vueResultats
    && (resultats.disponible === false || (resultats.echec && resultats.disponible === null));

  return (
    <div>
      {importOuvert && (
        <ImportResultats
          examen={examen}
          onRetour={() => setImportOuvert(false)}
          onTermine={() => {
            setImportOuvert(false);
            resultats.recharger();
          }}
          notifier={notifier}
        />
      )}

      {/* La liste des élèves est la vue d'arrivée : c'est là que se fait le travail. */}
      {!showStudentFile && !importOuvert && (
        <div>
          <FilArianeExamen
            codeCourant={code}
            libelleCourant={libelleLong}
            onRetour={onRetourTableauBord}
            onChangerExamen={onChangerExamen}
          />

          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-[#246990]">Élèves de {libelleClasses}</h2>
              <p className="text-sm text-gray-500">
                {filteredStudents.length} élève{filteredStudents.length > 1 ? 's' : ''}
                {filteredStudents.length !== eleves.length && ` sur ${eleves.length}`}
              </p>
              {vueResultats && resultats.dernierImport && (
                <p className="text-[11px] text-gray-500">
                  Dernier import : {resultats.dernierImport.nom_fichier || 'liste officielle'}
                  {resultats.dernierImport.importe_le
                    && `, le ${new Date(resultats.dernierImport.importe_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}
                  {resultats.dernierImport.importe_par_nom && ` par ${resultats.dernierImport.importe_par_nom}`}
                  {` - ${resultats.dernierImport.resultats_ecrits} résultat${resultats.dernierImport.resultats_ecrits > 1 ? 's' : ''}`}
                </p>
              )}
            </div>
            {/* La barre d'outils : toutes les commandes à la même hauteur (h-9),
                le même arrondi et la même taille de texte. L'action principale
                vient en dernier, seule en bleu plein. */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Dossiers ou résultats : la même liste, deux jeux de colonnes.
                  Bascule claire (fond gris, choix actif en blanc) : deux bleus
                  pleins côte à côte, la bascule et le bouton d'import, se
                  disputaient le regard. */}
              <div className="inline-flex h-9 items-center rounded-lg bg-gray-100 p-1" role="tablist" aria-label="Vue de la liste">
                {[
                  { valeur: 'dossiers', libelle: 'Dossiers' },
                  { valeur: 'resultats', libelle: 'Résultats' }
                ].map((choix) => (
                  <button
                    key={choix.valeur}
                    type="button"
                    role="tab"
                    aria-selected={vue === choix.valeur}
                    onClick={() => setVue(choix.valeur)}
                    className={`inline-flex h-7 min-w-[96px] items-center justify-center rounded-md px-3 text-sm font-semibold transition-colors ${vue === choix.valeur
                      ? 'bg-white text-[#246990] shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {choix.libelle}
                  </button>
                ))}
              </div>

              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder={vue === 'resultats' ? 'Nom ou matricule national' : 'Rechercher un élève'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 w-64 rounded-lg border-gray-200 pl-9 text-sm"
                />
              </div>

              {vue === 'resultats' ? (
                <ChampSelect
                  valeur={filtreResultat}
                  onChange={setFiltreResultat}
                  options={FILTRES_RESULTAT}
                  className="w-48"
                />
              ) : (
                <ChampSelect
                  valeur={statusFilter}
                  onChange={setStatusFilter}
                  options={STATUTS_DOSSIER}
                  className="w-48"
                />
              )}

              {/* La liste officielle se lit d'un coup, plutôt que soixante clics. */}
              {vue === 'resultats' && resultats.disponible && resultats.peutSaisir && (
                <Button
                  onClick={() => setImportOuvert(true)}
                  className="h-9 gap-2 rounded-lg bg-[#246990] px-4 text-sm text-white hover:bg-[#1f5a7a]"
                >
                  <Upload size={16} />
                  Importer la liste officielle
                </Button>
              )}

              {/* L'export produit la liste des dossiers : il n'a pas sa place
                  dans la vue Résultats, où il se lirait comme un palmarès. */}
              {vue === 'dossiers' && (
                <Button
                  onClick={handleExporter}
                  disabled={exportEnCours || filteredStudents.length === 0}
                  className="h-9 gap-2 rounded-lg bg-[#246990] px-4 text-sm text-white hover:bg-[#1f5a7a] disabled:opacity-50"
                >
                  {exportEnCours
                    ? <Loader2 size={16} className="animate-spin" />
                    : <Download size={16} />}
                  Exporter
                </Button>
              )}
            </div>
          </div>

          {/* Filtre par classe.
              Masqué quand il n'y en a qu'une — le CEPE n'a que le CM2, et une
              puce unique n'offrirait aucun choix. */}
          {classes.length > 1 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <PuceClasse
                libelle="Toutes"
                nombre={eleves.length}
                active={!selectedClass}
                onClick={() => setSelectedClass(null)}
              />
              {classes.map((nom) => (
                <PuceClasse
                  key={nom}
                  libelle={nom}
                  nombre={eleves.filter((e) => e.classe_nom === nom).length}
                  active={selectedClass === nom}
                  onClick={() => setSelectedClass(selectedClass === nom ? null : nom)}
                />
              ))}
            </div>
          )}

          {vueResultats && resultats.disponible && resultats.synthese && (
            <SyntheseResultats synthese={resultats.synthese} libelleClasses={libelleClasses} />
          )}

          {vueResultats && resultats.disponible && !resultats.peutSaisir && (
            <div className="mb-4 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-gray-600">
              Consultation seule : saisir les résultats demande le droit « Saisir les résultats des
              examens nationaux », que votre compte n&apos;a pas.
            </div>
          )}

          {vueResultats && resultats.disponible === false ? (
            // La migration n'est pas passée sur cette école : le dire, plutôt
            // qu'une liste de « non renseigné » que personne ne pourrait remplir.
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold">La saisie des résultats n&apos;est pas encore installée dans cette école.</p>
              <p className="mt-1">
                Elle sera disponible après la prochaine mise à jour. Les dossiers restent accessibles dans la vue Dossiers.
              </p>
            </div>
          ) : vueResultats && resultats.echec && resultats.disponible === null ? (
            <Card className="relative overflow-hidden">
              <EtatVide
                icone={XCircle}
                titre="Les résultats n'ont pas pu être lus"
                description="La liste des élèves reste disponible dans la vue Dossiers."
                action={(
                  <Button variant="outline" size="sm" onClick={resultats.recharger}>
                    Réessayer
                  </Button>
                )}
              />
            </Card>
          ) : (
          <Card className="relative overflow-hidden">

            {premierChargement || resultatsEnAttente ? (
              <LoadingSpinner message={resultatsEnAttente ? 'Chargement des résultats…' : 'Chargement des élèves…'} />
            ) : filteredStudents.length === 0 ? (
              <EtatVide
                icone={Users}
                titre={eleves.length === 0
                  ? `Aucun élève inscrit en ${libelleClasses}`
                  : 'Aucun élève ne correspond aux filtres'}
                description={eleves.length === 0
                  ? `Les élèves de ${libelleClasses} apparaîtront ici dès qu'ils seront enregistrés pour cette campagne.`
                  : `Élargissez la recherche, ou retirez le filtre de ${vueResultats ? 'résultat' : 'statut'} ou de classe.`}
                action={eleves.length > 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setFiltreResultat('all');
                      setSelectedClass(null);
                    }}
                  >
                    Retirer les filtres
                  </Button>
                ) : null}
              />
            ) : vueResultats ? (
              <TableauResultats
                eleves={elevesPage}
                parEleve={resultats.parEleve}
                peutSaisir={resultats.peutSaisir}
                libelleLong={libelleLong}
                onEnregistrer={resultats.enregistrer}
                notifier={notifier}
              />
            ) : (
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Élève
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Documents
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Paiement
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Statut
                      </th>
                      {/* Largeur fixe : sinon les boutons se decalent d'une ligne a l'autre. */}
                      <th className="w-[150px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {elevesPage.map((student) => {
                      const statut = calculateStatut(student);
                      return (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                {student.nom.charAt(0)}
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">
                                  {student.nom} {student.prenom}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {student.matricule}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {student.documents_fournis_count}/{student.documents_requis_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {/* Largeur fixe : repete d'une ligne a l'autre, un badge
                                libre forme une colonne irreguliere. */}
                            <span className={`inline-flex items-center justify-center w-[132px] h-6 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                              student.statut_paiement === 'paye' ? 'bg-green-100 text-green-800' :
                              student.statut_paiement === 'partiel' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {student.statut_paiement === 'paye' ? 'Payé' :
                               student.statut_paiement === 'partiel' ? 'Partiel' :
                               'Non payé'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getStatusBadge(statut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleStudentSelect(student)}
                              className="border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white"
                            >
                              <FileText size={16} className="mr-1" />
                              Voir le dossier
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
            )}
          </Card>
          )}

          {!listeMasquee && (
            <div className="mt-4">
              <Pagination
                page={page}
                nombrePages={nombrePages}
                total={filteredStudents.length}
                parPage={PAR_PAGE}
                onChangerPage={setPage}
                libelleElement="élève"
              />
            </div>
          )}
        </div>
      )}
      
      {/* Afficher le dossier d'un élève */}
      {showStudentFile && selectedStudent && (
        <div>
          {/* Trois niveaux au lieu de quatre : la classe n'est plus une etape,
              elle reste indiquee en precision mais ne se clique plus. */}
          <FilArianeExamen
            codeCourant={code}
            libelleCourant={libelleLong}
            onRetour={onRetourTableauBord}
            onRetourNiveau={handleBackToStudents}
            enfant={`${selectedStudent.nom} ${selectedStudent.prenom}`}
            precisionEnfant={selectedStudent.classe_nom}
          />
          
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#246990]">
              Dossier {libelleLong} - {selectedStudent.nom} {selectedStudent.prenom}
            </h2>
          </div>

          {loadingDetails ? (
            <LoadingSpinner message="Chargement du dossier…" />
          ) : detailsInscription ? (
            <div className="space-y-6">
              {/* Carte des documents */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#246990]">Documents requis</CardTitle>
                  <CardDescription>
                    Liste des documents nécessaires pour l'inscription au {libelleLong}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Pièce exigée
                          </th>
                          <th className="w-[220px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Réception
                          </th>
                          <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Fichier joint <span className="font-normal normal-case">(facultatif)</span>
                          </th>
                          <th className="w-[150px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {detailsInscription.documents_requis.map((docRequis) => {
                          const docFourni = detailsInscription.documents_fournis.find(
                            df => df.document_requis_id === docRequis.id
                          );
                          const recue = Boolean(docFourni);
                          const avecFichier = Boolean(docFourni?.nom_fichier);
                          const receptionEnCoursIci = receptionEnCours?.pieceId === docRequis.id;
                          const attendRecue = receptionEnCoursIci && receptionEnCours.recue;
                          const attendRetrait = receptionEnCoursIci && !receptionEnCours.recue;
                          const depotEnCoursIci = depotEnCours === docRequis.id;

                          return (
                            <tr key={docRequis.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm">
                                <p className="font-semibold text-gray-900">
                                  {docRequis.nom_document}
                                  {docRequis.obligatoire && <span className="ml-1 text-red-500">*</span>}
                                </p>
                                {docRequis.description && (
                                  <p className="mt-1 text-xs text-gray-500">{docRequis.description}</p>
                                )}
                              </td>

                              {/* Réception : l'action principale.
                                  Une pièce reçue au guichet se constate en un clic,
                                  sans avoir à la scanner d'abord. */}
                              <td className="px-6 py-4 text-center">
                                <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
                                  <button
                                    type="button"
                                    disabled={receptionEnCoursIci}
                                    onClick={() => handleMarquerReception(docRequis.id, true)}
                                    className={`inline-flex h-8 w-[86px] items-center justify-center gap-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${recue
                                      ? 'bg-[#246990] text-white'
                                      : 'bg-white text-gray-600 hover:bg-gray-50'
                                      }`}
                                  >
                                    {attendRecue
                                      ? <Loader2 size={13} className="animate-spin" />
                                      : <CheckCircle size={13} />}
                                    Reçue
                                  </button>
                                  <button
                                    type="button"
                                    disabled={receptionEnCoursIci}
                                    onClick={() => handleMarquerReception(docRequis.id, false)}
                                    className={`inline-flex h-8 w-[86px] items-center justify-center gap-1.5 border-l border-gray-200 text-xs font-semibold transition-colors disabled:opacity-50 ${!recue
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-white text-gray-500 hover:bg-gray-50'
                                      }`}
                                  >
                                    {attendRetrait
                                      ? <Loader2 size={13} className="animate-spin" />
                                      : <XCircle size={13} />}
                                    Non
                                  </button>
                                </div>
                                {recue && !avecFichier && (
                                  <p className="mt-1 text-[10px] text-gray-400">Non numérisée</p>
                                )}
                              </td>

                              <td className="px-6 py-4 text-sm">
                                {avecFichier ? (
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <FileText size={16} className="text-gray-600" />
                                      <span className="text-sm">{docFourni.nom_fichier}</span>
                                    </div>
                                    {docFourni.date_depot && (
                                      <p className="mt-1 text-xs text-gray-500">
                                        {new Date(docFourni.date_depot).toLocaleString('fr-FR')}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <input
                                      type="file"
                                      id={`file-${docRequis.id}`}
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleUploadDocument(docRequis.id, e.target.files[0]);
                                        }
                                      }}
                                      disabled={depotEnCoursIci}
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => document.getElementById(`file-${docRequis.id}`).click()}
                                      disabled={depotEnCoursIci}
                                      className="gap-1.5"
                                    >
                                      {depotEnCoursIci
                                        ? <Loader2 size={14} className="animate-spin" />
                                        : <Upload size={14} />}
                                      Joindre un fichier
                                    </Button>
                                  </div>
                                )}
                              </td>

                              <td className="px-6 py-4 text-center">
                                {avecFichier && (
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => window.open(docFourni.chemin_fichier, '_blank')}
                                      className="text-[#246990] hover:bg-[#eef7fb]"
                                    >
                                      <Eye size={14} />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteDocumentClick(docFourni)}
                                      className="text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Carte des frais d'inscription */}
              {detailsInscription.paiement && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-[#246990] flex items-center">
                          Frais d'inscription
                        </CardTitle>
                        <CardDescription>Suivi des paiements pour l'examen {libelleLong}</CardDescription>
                      </div>
                     <Button
                      size="sm"
                      onClick={handleOpenPaymentModal}
                      className={`text-white transition-colors ${
                        detailsInscription.paiement.statut === 'paye'
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-[#c66d59] hover:bg-[#ae7467]/90'
                      }`}
                      disabled={detailsInscription.paiement.statut === 'paye'}
                    >
                      <CreditCard size={16} className="mr-2" />
                      Enregistrer un paiement
                    </Button>

                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Montant attendu</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                          {formaterFCFA(detailsInscription.paiement.montant_attendu)}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Déjà versé</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                          {formaterFCFA(detailsInscription.paiement.montant_paye)}
                        </p>
                      </div>
                      {/* Le seul chiffre en gras fort : c'est lui qui commande l'action. */}
                      <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reste dû</p>
                        <p className="mt-1 text-2xl font-bold tabular-nums text-[#246990]">
                          {formaterFCFA(
                            Number(detailsInscription.paiement.montant_attendu || 0)
                            - Number(detailsInscription.paiement.montant_paye || 0)
                          )}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Statut</p>
                        <span className={`mt-2 inline-flex h-6 w-[132px] items-center justify-center rounded-full text-[10px] font-semibold whitespace-nowrap ${
                          detailsInscription.paiement.statut === 'paye' ? 'bg-green-100 text-green-800'
                            : detailsInscription.paiement.statut === 'partiel' ? 'bg-orange-100 text-orange-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {detailsInscription.paiement.statut === 'paye' ? 'Payé'
                            : detailsInscription.paiement.statut === 'partiel' ? 'Partiel'
                              : 'Non payé'}
                        </span>
                      </div>
                    </div>

                    {/* Historique si disponible */}
                    {detailsInscription.paiement.date_paiement && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-gray-700">Dernier paiement</p>
                          {detailsInscription.paiement.statut === 'paye' && (
                            <span className="flex items-center text-green-600 text-sm">
                              <CheckCircle size={16} className="mr-1" />
                              Paiement complet
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {detailsInscription.paiement.date_paiement && (
                            <div className="flex items-start">
                              <Calendar size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Date de paiement</span>
                                <span className="font-medium">
                                  {new Date(detailsInscription.paiement.date_paiement).toLocaleString('fr-FR', {
                                    dateStyle: 'long',
                                    timeStyle: 'short'
                                  })}
                                </span>
                              </div>
                            </div>
                          )}
                          {detailsInscription.paiement.mode_paiement && (
                            <div className="flex items-start">
                              <CreditCard size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Mode de paiement</span>
                                <span className="font-medium capitalize">
                                  {detailsInscription.paiement.mode_paiement.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          )}
                          {detailsInscription.paiement.enregistre_par_nom && (
                            <div className="flex items-start">
                              <User size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Enregistré par</span>
                                <span className="font-medium">
                                  {detailsInscription.paiement.enregistre_par_nom}
                                </span>
                              </div>
                            </div>
                          )}
                          {detailsInscription.paiement.reference_paiement && (
                            <div className="flex items-start">
                              <FileText size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Référence</span>
                                <span className="font-medium">{detailsInscription.paiement.reference_paiement}</span>
                              </div>
                            </div>
                          )}
                        </div>
                        {detailsInscription.paiement.notes && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="text-gray-600 text-sm block mb-1">Notes / Commentaires :</span>
                            <p className="text-sm bg-white p-2 rounded">{detailsInscription.paiement.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Modal Enregistrer un paiement */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
                <CreditCard size={20} />
              </span>
              Enregistrer un paiement
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Frais d&apos;examen {libelleLong} — {selectedStudent?.nom} {selectedStudent?.prenom}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Ce qui reste du, en tete : c'est le chiffre qui commande la saisie. */}
            {detailsInscription?.paiement && (
              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Attendu</p>
                    <p className="font-semibold tabular-nums text-gray-900">
                      {formaterFCFA(detailsInscription.paiement.montant_attendu)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Deja verse</p>
                    <p className="font-semibold tabular-nums text-gray-900">
                      {formaterFCFA(detailsInscription.paiement.montant_paye)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reste du</p>
                    <p className="font-semibold tabular-nums text-[#246990]">
                      {formaterFCFA(
                        Number(detailsInscription.paiement.montant_attendu || 0)
                        - Number(detailsInscription.paiement.montant_paye || 0)
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Deux colonnes : large plutot que long. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="montant_paye" className="text-sm text-gray-700">
                  Montant verse (FCFA) *
                </Label>
                <Input
                  id="montant_paye"
                  type="number"
                  min="0"
                  placeholder="Ex: 15000"
                  value={paiementForm.montant_paye}
                  onChange={(e) => setPaiementForm({ ...paiementForm, montant_paye: e.target.value })}
                  className="h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode_paiement" className="text-sm text-gray-700">
                  Mode de paiement *
                </Label>
                <ChampSelect
                  id="mode_paiement"
                  valeur={paiementForm.mode_paiement}
                  onChange={(valeur) => setPaiementForm({ ...paiementForm, mode_paiement: valeur })}
                  options={MODES_PAIEMENT}
                  className="w-full"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="reference_paiement" className="text-sm text-gray-700">
                  Reference de paiement
                </Label>
                <Input
                  id="reference_paiement"
                  placeholder="Ex: CHQ123456 ou numero de transaction"
                  value={paiementForm.reference_paiement}
                  onChange={(e) => setPaiementForm({ ...paiementForm, reference_paiement: e.target.value })}
                  className="h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="notes" className="text-sm text-gray-700">Notes</Label>
                <textarea
                  id="notes"
                  rows={3}
                  placeholder="Observations eventuelles..."
                  value={paiementForm.notes}
                  onChange={(e) => setPaiementForm({ ...paiementForm, notes: e.target.value })}
                  className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPaymentModal(false)}
              className={BOUTON_PIED_CLASSES}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSavePayment}
              disabled={ecritureEnCours}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
            >
              {ecritureEnCours
                ? <Loader2 size={16} className="animate-spin" />
                : <CheckCircle size={16} />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmation suppression document */}
      {/* ConfirmDialog plutot qu'un Dialog monte a la main : c'est le composant
          de confirmation du projet, et il aligne les deux boutons de pied a la
          meme largeur -- ce que la version maison ne faisait pas. */}
      <ConfirmDialog
        isOpen={showDeleteDocModal}
        isLoading={suppressionEnCours}
        onClose={() => {
          setShowDeleteDocModal(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleConfirmDeleteDocument}
        type="error"
        title="Supprimer cette pièce ?"
        description={`« ${documentToDelete?.nom_fichier || 'Ce document'} » sera définitivement retiré du dossier. Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />


      {/* Notification : ToastPortal pour sortir de la pile d'un Dialog, et
          bascule automatique en LongToast quand le message du serveur est long. */}
      <PorteurNotification notification={notification} onFermer={masquer} />

    </div>
  );
};

export default SectionExamen;